import { exportToCSV, exportToPDF } from '../../../../utils/exportUtils';
import AppCard from '../../../../components/ui/AppCard';
import React, {useState, useRef} from 'react';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import apiClient from "../../../../services/api-client";
import {Dialog} from 'primereact/dialog';
import {useMountEffect} from 'primereact/hooks';
import {Toast} from 'primereact/toast';
import StorageHelper from '../../../../utils/storage-helper';
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import ViewMainMemberTable from "./components/ViewMainMemberTable.jsx";
import ViewSpouseMemberTable from "./components/ViewSpouseMemberTable.jsx";
import ViewMemberBeneficiaries from "./components/ViewMemberBeneficiaries.jsx";
import ViewChildBeneficiaries from "./components/ViewChildBeneficiaries.jsx";
import ViewExtendedBeneficiaryDetails from "./components/ViewExtendedBeneficiaryDetails.jsx";
import ViewBankingDetails from "./components/ViewBankingDetails.jsx";
import {approveApplication, rejectApplication} from "../../../../services/application-service";

const ApprovePolicyTable = () => {
    const [Policies, setPolicies] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [activePolicy, setActivePolicy] = useState(null);
    const [dialogPolicyVisible, setPolicyDialogVisible] = useState(false);
    const [confirmApproveVisible, setConfirmApproveVisible] = useState(false);
    const [confirmRejectVisible, setConfirmRejectVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const dt = useRef(null);
    const toast = useRef(null);

    const userRole = StorageHelper.get('userRole');
    const roleName = userRole?.name || '';
    const isAuthorized = ['ADMIN', 'UNDERWRITER', 'SUPER_ADMIN'].includes(roleName.toUpperCase());

    const status = activePolicy?.applicationStatus?.toUpperCase() || '';
    const canModify = status === 'WAITING_APPROVAL' || status === 'WAITING-APPROVAL';

    const handleApprove = async () => {
        const appId = activePolicy?.policyApplicationRequestId || activePolicy?.id;
        if (!appId) {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Application ID is missing.'
                });
            }
            return;
        }

        setSubmitting(true);
        try {
            const response = await approveApplication(appId);
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data?.message || 'Application approved successfully.'
                });
            }
            setConfirmApproveVisible(false);
            setPolicyDialogVisible(false);
            fetchPolicies();
        } catch (error) {
            console.error('Error approving application:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || 'Failed to approve application.'
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        const appId = activePolicy?.policyApplicationRequestId || activePolicy?.id;
        if (!appId) {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Application ID is missing.'
                });
            }
            return;
        }

        setSubmitting(true);
        try {
            const response = await rejectApplication(appId);
            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data?.message || 'Application rejected successfully.'
                });
            }
            setConfirmRejectVisible(false);
            setPolicyDialogVisible(false);
            fetchPolicies();
        } catch (error) {
            console.error('Error rejecting application:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || 'Failed to reject application.'
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const cols = [
        {field: 'easipolPolicyNumber', header: 'Policy Number'},
        {field: 'mainMember.First_Name', header: 'First name'},
        {field: 'mainMember.Surname', header: 'Last name'},
        {field: 'productName', header: 'Product Name'},
        {field: 'applicationStatus', header: 'Status'},
        {field: 'open-policy', header: 'Actions'}
    ];

    useMountEffect(() => {
        fetchPolicies();
    }, []);

    const fetchPolicies = async () => {
        try {
            const response = await apiClient.get('policy/list/applications', {
                headers: {
                    Authorization: 'Bearer ' + StorageHelper.get('token')
                }
            });

            if (response.data.status === "SUCCESS") {
                setPolicies(response.data.responseBody);
            }
        } catch (error) {
            console.error('Error fetching policies:', error);
        }
    };

    const renderOpenPolicyAction = (rowData) => {
        return (
            <div className="actions-container flex gap-2">
                <div className="tooltip" data-tip="View Full Policy">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => {
                            setActivePolicy(rowData);
                            setPolicyDialogVisible(true);
                        }}
                    >
                        <i className="pi pi-eye text-lg"></i>
                    </button>
                </div>
            </div>
        );
    };

    const applicationStatusBodyTemplate = (rowData) => {
        const status = rowData.applicationStatus;
        if (!status) return null;
        const upperStatus = status.toUpperCase();
        let color = 'badge-ghost';
        if (upperStatus.includes('WAITING') || upperStatus === 'PENDING') {
            color = 'badge-warning';
        } else if (upperStatus === 'APPROVED' || upperStatus === 'ACTIVE' || status === 'submitted-in-easipol') {
            color = 'badge-success';
        } else if (upperStatus === 'DECLINED' || upperStatus === 'REJECTED' || status === 'rejected-policy-application') {
            color = 'badge-error';
        }
        return <span className={`badge badge-outline font-semibold text-xs uppercase ${color}`}>{status}</span>;
    };


    const globalFilterElement = (
        <div className="relative w-full max-w-xs">
            <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
            <input
                type="text"
                className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
                placeholder="Search..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
            />
        </div>
    );

    const header = (
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center w-full">
            {globalFilterElement}
            <div className="flex items-center gap-3 ml-auto">
                <div className="dropdown dropdown-end">
                    <div tabIndex={0} role="button"
                         className="btn btn-outline btn-ghost h-10 min-h-0 shadow-sm rounded-full">
                        <i className="pi pi-file-export"></i> Export
                    </div>
                    <ul tabIndex={0} className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                                    <li><button type="button" onClick={() => exportToCSV(dt, 'Policies')}><i className="pi pi-file-excel text-success"></i> Download CSV</button></li>
                                    <li><button type="button" onClick={() => exportToPDF(dt, 'Policies', 'Policies Report')}><i className="pi pi-file-pdf text-error"></i> Download PDF</button></li>
                                </ul>
                </div>
            </div>
        </div>
    );

    return (
        <div className="w-full">
            <Toast ref={toast}/>
            <Dialog
                header="Full Policy Detail"
                visible={dialogPolicyVisible}
                maximizable
                style={{width: '60vw'}}
                onHide={() => {
                    setPolicyDialogVisible(false);
                    setActivePolicy(null);
                }}
            >
                {activePolicy && (
                    <>
                        <ViewMainMemberTable activeUserData={activePolicy}/>
                        <ViewSpouseMemberTable activeUserData={activePolicy}/>
                        <ViewChildBeneficiaries activeUserData={activePolicy}/>
                        <ViewExtendedBeneficiaryDetails activeUserData={activePolicy}/>
                        <ViewMemberBeneficiaries activeUserData={activePolicy}/>
                        <ViewBankingDetails activeUserData={activePolicy}/>

                        {isAuthorized && canModify && (
                            <div className="flex justify-content-end gap-3 mt-4 p-3 border-top-1 surface-border">
                                <Button
                                    label="Reject Application"
                                    icon="pi pi-times"
                                    severity="danger"
                                    disabled={submitting}
                                    className="p-button-danger p-button-outlined"
                                    onClick={() => setConfirmRejectVisible(true)}
                                />
                                <Button
                                    label="Approve Application"
                                    icon="pi pi-check"
                                    severity="success"
                                    disabled={submitting}
                                    className="p-button-success"
                                    onClick={() => setConfirmApproveVisible(true)}
                                />
                            </div>
                        )}
                    </>
                )}
            </Dialog>

            {/* Approve Confirmation Dialog */}
            <Dialog
                header="Confirm Approval"
                visible={confirmApproveVisible}
                style={{width: '450px'}}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            disabled={submitting}
                            onClick={() => setConfirmApproveVisible(false)}
                        />
                        <Button
                            label="Confirm Approval"
                            icon="pi pi-check"
                            className="p-button-success"
                            loading={submitting}
                            onClick={handleApprove}
                        />
                    </div>
                }
                onHide={() => setConfirmApproveVisible(false)}
            >
                <div className="flex align-items-center justify-content-center p-3">
                    <i className="pi pi-exclamation-triangle text-warning mr-3" style={{fontSize: '2rem'}}/>
                    <span>Are you sure you want to approve this application?</span>
                </div>
            </Dialog>

            {/* Reject Confirmation Dialog */}
            <Dialog
                header="Confirm Rejection"
                visible={confirmRejectVisible}
                style={{width: '450px'}}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            disabled={submitting}
                            onClick={() => setConfirmRejectVisible(false)}
                        />
                        <Button
                            label="Confirm Rejection"
                            icon="pi pi-check"
                            className="p-button-danger"
                            loading={submitting}
                            onClick={handleReject}
                        />
                    </div>
                }
                onHide={() => setConfirmRejectVisible(false)}
            >
                <div className="flex align-items-center justify-content-center p-3">
                    <i className="pi pi-exclamation-triangle text-danger mr-3" style={{fontSize: '2rem'}}/>
                    <span>Are you sure you want to reject this application?</span>
                </div>
            </Dialog>

            <AppCard title="Policy Applications">
                <DataTable
                    header={header}
                    paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                    paginator
                    rows={5}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    ref={dt}
                    value={Policies}
                    tableStyle={{minWidth: '50rem'}}
                    globalFilter={globalFilter}
                    emptyMessage="No Policies found"
                >
                    {cols.map((col, index) => {
                        if (col.field === 'open-policy') {
                            return (
                                <Column
                                    key={index}
                                    body={renderOpenPolicyAction}
                                    header={col.header}
                                    style={{textAlign: 'center'}}
                                />
                            );
                        }
                        return (
                            <Column key={index} field={col.field} header={col.header}
                                    body={col.field === 'applicationStatus' ? applicationStatusBodyTemplate : null}/>
                        );
                    })}
                </DataTable>
            </AppCard>
        </div>
    );
};

export default ApprovePolicyTable;